import React, { useEffect, useState } from 'react';
import Topbar from '../../Components/Topbar';
import LeftbarUser from '../../Components/LeftbarUser';
import BottomNavUser from '../../Components/BottomNavUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { app } from '../../firebase';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Button, Spinner } from 'react-bootstrap';
import swal from 'sweetalert';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import defProfile from '../../Images/def.png';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import ProgressBar from 'react-bootstrap/ProgressBar';

function SubmitAds() {
    const location = useLocation();
    const navigate = useNavigate();
    const db = getFirestore(app);
    const auth = getAuth();

    const { platform, tier, category, adType, influencerIds = [] } = location.state || {};

    const [influencersData, setInfluencersData] = useState([]);
    const [loggedInUserId, setLoggedInUserId] = useState('');
    const [loggedInUserName, setLoggedInUserName] = useState('');
    const [loading, setLoading] = useState(true);

    const [brandName, setBrandName] = useState('');
    const [businessDesc, setBusinessDesc] = useState('');
    const [websiteLink, setWebsiteLink] = useState('');
    const [adPurpose, setAdPurpose] = useState('');
    const [postCaption, setPostCaption] = useState('');
    const [cgcDetails, setCgcDetails] = useState('');

    const [uploadType, setUploadType] = useState('image');
    const [filePreview, setFilePreview] = useState(null);
    const [videoError, setVideoError] = useState('');

    const [showProgress, setShowProgress] = useState(false);
    const [now, setNow] = useState(0);

    const [adDays, setAdDays] = useState(1);
    const [totalCost, setTotalCost] = useState(0);

    const [loadingBtn, setLoadingBtn] = useState(false);
    const [makePaymentBtn, setMakePaymentBtn] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    useEffect(() => {
        document.title = "Zindua App | Dashboard";

        // Meta description
        let desc = document.querySelector("meta[name='description']");
        if (!desc) {
            desc = document.createElement("meta");
            desc.setAttribute("name", "description");
            document.head.appendChild(desc);
        }
        desc.setAttribute("content", "Zindua App dashboard");

        // Open Graph tags
        const ogTags = [
            { property: "og:title", content: "Zindua App | Dashboard" },
            { property: "og:description", content: "Zindua App Dashboard" },
            { property: "og:type", content: "website" },
            { property: "og:url", content: "https://zindua.app/dashboard" },
            { property: "og:image", content: "https://zindua.app/logo192.png" },
        ];

        ogTags.forEach(({ property, content }) => {
            let tag = document.querySelector(`meta[property='${property}']`);
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute("property", property);
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", content);
        });

        // JSON-LD structured data
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Zindua App Influencer Dashboard",
            "url": "https://zindua.app/dashboard",
            "description": "Zindua App influencer dashboard where you can manage your earnings, running ads, subscriptions, and withdraw funds to M-Pesa.",
            "inLanguage": "en",
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://zindua.app/"
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Dashboard",
                        "item": "https://zindua.app/dashboard"
                    }
                ]
            }
        });
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);


    const [step, setStep] = useState(1);
    const [mpesaNumber, setMpesaNumber] = useState('');

    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const [paymentTransactionId, setPaymentTransactionId] = useState(null);
    const [paymentCountdown, setPaymentCountdown] = useState(180);


    const baseUrl = 'https://us-central1-zindua-ke.cloudfunctions.net/paymentApi';

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async user => {
            if (!user) return;
            setLoggedInUserId(user.uid);
            const snap = await getDoc(doc(db, 'users', user.uid));
            if (snap.exists()) setLoggedInUserName(snap.data().userName || '');
        });
        return () => unsub();
    }, []);

    const getInfluencerCGCDeliveryTime = (influencer) => {
        if (adType !== 'cgc') return null

        return (
            influencer?.servicePricing?.[platform]?.creatorGeneratedContent?.deliveryTime ||
            '36 hours'
        )
    }


    /* ================= FETCH INFLUENCERS ================= */
    useEffect(() => {
        const fetchInfluencers = async () => {
            setLoading(true);
            try {
                const snaps = await Promise.all(
                    influencerIds.map(id => getDoc(doc(db, 'users', id)))
                );
                const data = snaps
                    .map((snap, i) => snap.exists() ? { id: influencerIds[i], ...snap.data() } : null)
                    .filter(Boolean);
                setInfluencersData(data);
            } catch {
                toast.error('Failed to load influencers');
            }
            setLoading(false);
        };
        fetchInfluencers();
    }, [influencerIds]);

    /* ================= COST CALCULATION ================= */
    const calculateInfluencerCost = (influencer) => {
        if (!influencer?.servicePricing) return 0;

        const platformPricing = influencer.servicePricing[platform] || {};
        let basePrice = 0;

        switch (adType) {
            case 'post':
                basePrice = platformPricing?.directBrandContent?.post ?? 0;
                break;
            case 'story':
                basePrice = platformPricing?.directBrandContent?.story ?? 0;
                break;
            case 'cgc':
                basePrice = platformPricing?.creatorGeneratedContent?.price ?? 0;
                break;
            default:
                basePrice = 0;
        }

        const chargedPerDay =
            (platform.toLowerCase() === 'twitter' && (adType === 'post' || adType === 'cgc')) ||
            (platform.toLowerCase() === 'instagram' && adType === 'story');

        return chargedPerDay ? basePrice * adDays : basePrice;
    };

    useEffect(() => {
        let sum = 0;

        influencersData.forEach(influencer => {
            const cost = calculateInfluencerCost(influencer);
            sum += cost;

            // Log each influencer's cost
            //console.log(`${influencer.userName || influencer.id}: KES ${Number(cost).toLocaleString()}`);
        });

        setTotalCost(sum);
        //console.log(`Total Cost: KES ${Number(sum).toLocaleString()}`);
    }, [influencersData, adDays, platform, adType]);

    useEffect(() => {
        const sum = influencersData.reduce(
            (acc, inf) => acc + calculateInfluencerCost(inf),
            0
        );
        setTotalCost(sum);
    }, [influencersData, adDays, platform, adType]);

    /* ================= FILE UPLOAD ================= */
    const handleFileUpload = e => {
        const file = e.target.files[0];
        if (!file) return;

        setVideoError('');
        const preview = URL.createObjectURL(file);
        setFilePreview(preview);

        if (file.type.startsWith('video')) {
            const video = document.createElement('video');
            video.src = preview;
            video.onloadedmetadata = () => {
                if (video.duration > 60) {
                    setVideoError('Video must be 60 seconds or less');
                    setFilePreview(null);
                }
            };
        }
    };

    /* ================= VALIDATION ================= */
    const validateForm = () => {
        if (!brandName || !businessDesc || !websiteLink || !adPurpose) {
            swal('Error', 'Please fill all required fields', 'error');
            return false;
        }
        if ((adType === 'post' || adType === 'story') && !filePreview) {
            swal('Error', 'Please upload your file', 'error');
            return false;
        }
        return true;
    };

    /* ================= PAYMENT LOGIC ================= */
    const startCountdown = () => {
        setPaymentCountdown(180);
        const interval = setInterval(() => {
            setPaymentCountdown(prev => {
                if (prev <= 1) { clearInterval(interval); toast.error("Payment timed out. Retrying STK push..."); retryPayment(paymentTransactionId); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const retryPayment = async transactionId => { if (transactionId) await verifyPayment(transactionId); };

    const makePayment = async number => {
        if (!validateForm()) return;
        setShowPaymentModal(true);
        setIsPaymentProcessing(true);
        setMakePaymentBtn(false);
        setLoadingBtn(true);
        try {
            const payload = {
                amount: 1,
                contact_phone_number: mpesaNumber,
                contact_name: loggedInUserName,
                description: `${adType} ad payment`
            };
            const res = await fetch(`${baseUrl}/make-payment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error("Payment request failed");
            const data = await res.json();
            const transactionId = data?.data?.data?.transaction?.id;
            setPaymentTransactionId(transactionId);
            toast.success("Payment request sent. Check your phone for STK prompt.");
            startCountdown();
            await verifyPayment(transactionId);
        } catch (err) {
            console.error("Payment error:", err);
            toast.error("Failed to initiate payment. Retrying...");
            setTimeout(() => makePayment(number), 3000);
        }
    };

    const verifyPayment = async transactionId => {
        try {
            const res = await fetch(`${baseUrl}/verify-payment/${transactionId}`);
            const data = await res.json();
            const tx = data?.data?.data?.transaction?.[0];
            if (!tx || tx.status === "pending") { setTimeout(() => verifyPayment(transactionId), 5000); return; }
            if (tx.status === "success") { setIsPaymentProcessing(false); setShowPaymentModal(false); processPayment(transactionId); toast.success("Payment successful!"); }
            else { setIsPaymentProcessing(false); setShowPaymentModal(false); swal("Payment failed", `Status: ${tx.status}`, "error"); }
        } catch (err) { console.error("Verification error:", err); setTimeout(() => verifyPayment(transactionId), 5000); }
    };

    /* ================= SUBMIT ================= */
    const processPayment = async (transactionId) => {
        if (!validateForm()) return;

        setLoadingBtn(true);
        const storage = getStorage(app);
        const file = document.querySelector('input[type="file"]')?.files[0];

        try {
            // 1. Create campaign
            const campaignRef = await addDoc(collection(db, 'campaigns'), {
                advertiserId: loggedInUserId,
                totalPaid: totalCost,
                influencerCount: influencersData.length,
                createdAt: Timestamp.now()
            });

            // 2. Upload file if exists
            let uploadedFiles = [];
            if (file) {
                const fileRef = storageRef(storage, `ads/common/${Date.now()}_${file.name}`);
                const uploadTask = uploadBytesResumable(fileRef, file);

                setShowProgress(true); // show progress bar
                uploadedFiles.push(await new Promise((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        snapshot => {
                            const progressPercent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            setNow(progressPercent);
                        },
                        error => reject(error),
                        async () => {
                            const url = await getDownloadURL(uploadTask.snapshot.ref);
                            setShowProgress(false); // hide progress bar when done
                            resolve(url);
                        }
                    );
                }));
            }

            // 3. Process each influencer
            for (const influencer of influencersData) {
                try {
                    const influencerTotal = calculateInfluencerCost(influencer);
                    const platformFee = influencerTotal * 0.2;
                    const payout = influencerTotal - platformFee;

                    // Add ad document
                    const adRef = await addDoc(collection(db, 'ads'), {
                        influencerId: influencer.id,
                        campaignId: campaignRef.id,
                        social: platform,
                        adType,
                        adDays,
                        totalCost: influencerTotal,
                        brandName,
                        businessDesc,
                        websiteLink,
                        adPurpose,
                        postCaption,
                        cgcDetails: adType === 'cgc' ? cgcDetails : null,
                        cgcDeliveryTime: adType === 'cgc'
                            ? getInfluencerCGCDeliveryTime(influencer)
                            : null,
                        uploadedFiles,
                        adStatus: 'Submitted',
                        timestamp: Timestamp.now(),
                        loggedInUserId,
                        businessCategory: category,
                        payMethod: "Mpesa Choice Bank",
                        currency: "KES",
                        responseReference: transactionId,
                        paymentStatus: "success",
                        uploadType: uploadType || null,
                        mpesaNumber: mpesaNumber || null

                    });


                    // Add income record
                    await addDoc(collection(db, 'income'), {
                        influencerId: influencer.id,
                        campaignId: campaignRef.id,
                        adID: adRef.id,
                        totalCost: influencerTotal,
                        payout,
                        earnings: platformFee,
                        payoutStatus: 'Pending',
                        paymentRef: transactionId,
                        timestamp: Timestamp.now(),
                        loggedInUserId,
                        mpesaNumber: mpesaNumber || null,
                        currency: "KES",
                        paymentReference: transactionId,
                        payMethod: "Mpesa Choice Bank",
                        incomeType: "adRequest",
                        adStatus: 'Requested'
                    });

                    // Send notifications
                    await sendInfluencerNotification({ influencerId: influencer.id });


                } catch (err) {
                    console.error(`Error processing influencer ${influencer.id}:`, err);
                    toast.warn(`Failed to submit ad for ${influencer.userName || influencer.id}`);
                }
            }

            await sendAdvertiserNotification(influencersData.length);

            swal('Success', 'Campaign submitted successfully', 'success')
                .then(() =>
                    navigate('/user/ads')
                    //send notification to advertiser 

                );


        } catch (err) {
            console.error("Error processing payment:", err);
            toast.error("Failed to submit campaign. Please try again.");
        } finally {
            setLoadingBtn(false);
        }
    };

    const sendInfluencerNotification = async ({ influencerId }) => {
        try {
            const influencerSnap = await getDoc(doc(db, "users", influencerId));
            if (!influencerSnap.exists()) return;

            const influencerData = influencerSnap.data();
            const token = await auth.currentUser.getIdToken();

            const requests = [];

            // Email to influencer
            if (influencerData.email) {
                requests.push(
                    fetch("https://us-central1-zindua-ke.cloudfunctions.net/commapi/send-email", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            email: influencerData.email,
                            subject: "NEW AD SUBMITTED",
                            message: `Hi ${influencerData.userName}, a new ad has been submitted for you by ${loggedInUserName} (Brand: ${brandName}). Please check your dashboard.`
                        })
                    })
                );
            }

            // SMS to influencer
            if (influencerData.mpesaNumber) {
                requests.push(
                    fetch("https://us-central1-zindua-ke.cloudfunctions.net/commapi/send-sms", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            phoneNumber: influencerData.mpesaNumber,
                            message: `Hi ${influencerData.userName}, you have a new ad request from ${loggedInUserName}. Please check your dashboard.`
                        })
                    })
                );
            }

            await Promise.all(requests);

        } catch (error) {
            console.error("Influencer notification error:", error);
        }
    };

    const sendAdvertiserNotification = async (influencerCount) => {
        try {
            const advertiserSnap = await getDoc(doc(db, "users", loggedInUserId));
            if (!advertiserSnap.exists()) return;

            const advertiserData = advertiserSnap.data();
            const token = await auth.currentUser.getIdToken();

            const requests = [];

            // Email to advertiser
            if (advertiserData.email) {
                requests.push(
                    fetch("https://us-central1-zindua-ke.cloudfunctions.net/commapi/send-email", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            email: advertiserData.email,
                            subject: "CAMPAIGN SUBMITTED SUCCESSFULLY",
                            message: `Hi ${advertiserData.userName}, your campaign for ${brandName} has been successfully submitted to ${influencerCount} influencers.`
                        })
                    })
                );
            }

            // SMS to advertiser
            if (advertiserData.mpesaNumber) {
                requests.push(
                    fetch("https://us-central1-zindua-ke.cloudfunctions.net/commapi/send-sms", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            phoneNumber: advertiserData.mpesaNumber,
                            message: `Your campaign for ${brandName} was sent to ${influencerCount} influencers successfully.`
                        })
                    })
                );
            }

            await Promise.all(requests);

        } catch (error) {
            console.error("Advertiser notification error:", error);
        }
    };


    return (
        <div className="mainDashboard">
            <div className="desktopOnly"><LeftbarUser /></div>
            <div className="mainContent">
                <Topbar />
                <div className="container mt-2">
                    <h4>Submit Your Ad Campaign</h4>
                    <div className='alertCont'>
                        <p><strong>Platform:</strong> {platform}</p>
                        <p><strong>Tier:</strong> {tier || "All Creator category"}</p>
                        <p><strong>Category:</strong> {category}</p>
                        <p><strong>Ad Type:</strong> {adType === 'cgc' ? 'Creator Generated Content' : adType === 'post' ? 'Media Upload' : ''}</p>
                        <p><strong>Total Cost:</strong> KES {Number(totalCost).toLocaleString()}</p>
                        <p><strong>Total Number of Influencers:</strong> {influencersData.length}</p>
                        {/* <p><strong>Selected Influencers:</strong> {influencersData.map(i => i.userName || i.id).join(', ')}</p> */}
                    </div>

                    <div className="adDetails container mt-3" style={{ display: step === 1 ? 'block' : 'none' }}>
                        <div className="row mb-2">

                            <div className="col-md-6">
                                <label className="form-label">Purpose of the Ad</label>
                                <select className="form-select" value={adPurpose} onChange={e => setAdPurpose(e.target.value)}>
                                    <option value="">Select objective</option>
                                    <option value="Drive Sales to my business">Drive Sales to your business</option>
                                    <option value="Introduce a product/Brand">Introduce a product/Brand</option>
                                    <option value="Creating awareness">Creating awareness</option>
                                    <option value="Brand positioning">Brand positioning</option>
                                    <option value="App Installs">App Installs</option>
                                    <option value="Website Visits">Website Visits</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Business/Brand name</label>
                                <input type="text" className="form-control" placeholder="Brand Name" value={brandName} onChange={e => setBrandName(e.target.value)} />
                            </div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-md-6">
                                <label className="form-label">What does the brand/business or service do</label>
                                <input type="text" className="form-control" placeholder="What the business or brand does" value={businessDesc} onChange={e => setBusinessDesc(e.target.value)} />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Paste your Website or social media link</label>
                                <input type="text" className="form-control" placeholder="Paste your link" value={websiteLink} onChange={e => setWebsiteLink(e.target.value)} />
                            </div>
                        </div>


                        <Button onClick={() => setStep(2)}>Next</Button>
                    </div>

                    <div className='submitContainer' style={{ display: step === 2 ? 'block' : 'none' }}>


                        {adType === 'cgc' && (
                            <>


                                <div className="mb-3">
                                    <label className="form-label">Give the influencer enough details to create your content</label>
                                    <textarea
                                        className="form-control"
                                        rows={4}
                                        value={cgcDetails}
                                        onChange={e => setCgcDetails(e.target.value)}
                                    />
                                </div>

                                {(platform === 'twitter' &&
                                    (adType === 'post' || adType === 'cgc')) && (
                                        <div className="mb-3">
                                            <label className="form-label">Number of days to run the ad: {adDays}</label>
                                            <input
                                                type="range"
                                                min={1}
                                                max={30}
                                                value={adDays}
                                                onChange={e => setAdDays(Number(e.target.value))}
                                                className="form-range"
                                            />
                                        </div>
                                    )}

                                <div className="alert alert-info">
                                    Your total ad cost will be KES {Number(totalCost).toLocaleString()}
                                </div>
                            </>
                        )}


                        {(adType === 'post' || adType === 'story') && (
                            <>

                                <div className="mb-3">
                                    <label className="form-label">What do you want to upload?</label>
                                    <select className="form-select" value={uploadType} onChange={e => setUploadType(e.target.value)}>
                                        <option value="image">Image</option>
                                        <option value="video">Video</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Upload your file</label>
                                    <label className="customFileUpload">
                                        <input
                                            type="file"
                                            accept={uploadType === "video" ? "video/*" : "image/*"}
                                            onChange={handleFileUpload}
                                        />
                                        <span>Click to upload file</span>
                                    </label>

                                    {videoError && (
                                        <p className="videoWarning">{videoError}</p>
                                    )}

                                    {filePreview && (
                                        <div className="previewBox">
                                            {uploadType === 'image' && (
                                                <img src={filePreview} alt="preview" className="previewMedia" />
                                            )}

                                            {uploadType === 'video' && (
                                                <video
                                                    src={filePreview}
                                                    className="previewMedia"
                                                    controls
                                                />
                                            )}
                                        </div>


                                    )}

                                </div>

                                <div className='mb-3'>
                                    <label className="form-label">Post caption</label>
                                    <textarea className="form-control" value={postCaption} onChange={e => setPostCaption(e.target.value)} />
                                </div>

                                {(platform === 'twitter' && (adType === 'post' || adType === 'cgc') || (platform === 'instagram' && adType === 'story')) && (
                                    <div className="mb-3">
                                        <label className="form-label">Number of days to run the ad: {adDays}</label>
                                        <input
                                            type="range"
                                            min={1}
                                            max={30}
                                            value={adDays}
                                            onChange={e => setAdDays(Number(e.target.value))}
                                            className="form-range"
                                        />
                                    </div>
                                )}

                                <div className="alert alert-info">
                                    Your total ad cost will be KES {Number(totalCost).toLocaleString()}
                                </div>
                            </>
                        )}
                        <Button onClick={() => setStep(1)}>Back</Button>

                        {makePaymentBtn &&
                            <Button
                                variant="success"
                                onClick={() => {
                                    if (validateForm()) {
                                        setShowPaymentModal(true);
                                    }
                                }}
                            >
                                Submit and Pay
                            </Button>

                        }

                        {loadingBtn && <Button variant="success" disabled className='btnLogin'>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            {" "}Submitting...
                        </Button>
                        }

                        {showProgress &&
                            <ProgressBar now={now} label={`${now}%`} />
                        }
                    </div>
                </div>
            </div>
            <div className="mobileOnly"><BottomNavUser /></div>
            <div className={`modal fade ${showPaymentModal ? "show" : ""}`} style={{ display: showPaymentModal ? "block" : "none", background: "rgba(0,0,0,0.5)" }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ borderRadius: "12px" }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Complete Payment</h5>
                            <button type="button" className="btn-close" disabled={isPaymentProcessing} onClick={() => !isPaymentProcessing && setShowPaymentModal(false)}></button>
                        </div>
                        <div className="modal-body text-center">
                            {isPaymentProcessing ? <>
                                <Spinner animation="border" size="lg" />
                                <p>Waiting for M-PESA payment...</p>
                                <p>Time left: {Math.floor(paymentCountdown / 60)}:{String(paymentCountdown % 60).padStart(2, "0")}</p>
                            </> : <>
                                <p>Enter your M-PESA number to receive a payment prompt.</p>
                                <input type="number" className="form-control" placeholder="07XX XXX XXX" value={mpesaNumber} onChange={e => setMpesaNumber(e.target.value)} />
                            </>}
                        </div>
                        {!isPaymentProcessing && <div className="modal-footer">
                            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
                            <Button variant="success" onClick={() => makePayment(mpesaNumber)}>Confirm and Pay</Button>
                        </div>}
                    </div>
                </div>
            </div>



            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="dark" transition={Bounce} />
        </div>
    );
}

export default SubmitAds;